import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { GitCompare } from 'lucide-react'

import { Button } from '@/components/ui'
import { useDocumentRetrieval } from '@/features/document'
import { isHTMLDocument, isPDFDocument } from '@/utils'
import { EDocumentType } from '@/utils/constants'

import { useGetVersionList } from '../hooks'
import { useVersionComparisonStore } from '../store'

type TComparisonButtonProps = {
  documentId: string // ID of the document to compare versions for
}

/**
 * Comparison Button Component
 * Renders a button that opens the version comparison dialog when clicked.
 * The button is disabled if:
 * - There are less than 2 versions (nothing to compare)
 * - Document type is not HTML or PDF (unsupported formats)
 *
 * @param documentId - The ID of the document to enable version comparison for
 */
const ComparisonButton: React.FC<TComparisonButtonProps> = ({ documentId }) => {
  const { t } = useTranslation()
  const { versions } = useGetVersionList({ documentId })
  const { setCompareVersionDocument } = useVersionComparisonStore()

  const { data: document } = useDocumentRetrieval(documentId)

  /**
   * Determine the document type for proper handling in the comparison viewer
   */
  const documentType = React.useMemo(() => {
    return isHTMLDocument(document?.filename)
      ? EDocumentType.HTML
      : EDocumentType.PDF
  }, [document?.filename])

  /**
   * Determine if button should be disabled
   * Disabled when: less than 2 versions OR unsupported document type
   */
  const isDisabled = React.useMemo(() => {
    return (
      versions.length <= 1 ||
      !(isHTMLDocument(document?.filename) || isPDFDocument(document?.filename))
    )
  }, [versions, document?.filename])

  const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    // Set document ID to open version selection dialog
    setCompareVersionDocument({
      documentId,
      documentType,
      documentFilename: document?.filename,
    })
  }

  return (
    <Button
      variant="outlined"
      color="default"
      onClick={handleOnClick}
      aria-label={t('versionComparison.button.compareVersion')}
      className="gap-2 whitespace-nowrap group py-2 px-3"
      disabled={isDisabled}
      leadingIcon={
        <GitCompare className="size-5 text-foreground-fg-quaternary-400 group-disabled:text-foreground-fg-quaternary-400 group-hover:text-quaternary-500" />
      }
    >
      {t('versionComparison.button.compareVersion')}
    </Button>
  )
}

/**
 * Memoized version of ComparisonButton to prevent unnecessary re-renders
 * when parent components update but props haven't changed
 */
const ComparisonButtonMemo = React.memo(ComparisonButton)
ComparisonButtonMemo.displayName = 'ComparisonButton'

export { ComparisonButtonMemo as ComparisonButton }
